import axiosInstance from '../../core/axiosConfig.ts';
import * as Sentry from '@sentry/react';
import type { OrderSource } from '../../components/customer/CustomerChips.tsx';
import type { DineInInfo, OrderType } from '../../components/order/DineIn.tsx';

/**
 * Order history (23 Oct 2026): Today's orders and Past orders in one page.
 * The server decides the scope - a restaurant login only ever gets its own
 * restaurant and the food total - and pages, filters and counts the range.
 */

export type OrderHistoryOutcome = 'OPEN' | 'DELIVERED' | 'REJECTED' | 'CANCELLED';

/** The order's own status; a restaurant never gets the courier step. */
export type OrderStatus =
    | 'PAID' | 'ACCEPTED' | 'IN_PREPARATION' | 'READY_FOR_PICKUP' | 'ACCEPTED_BY_COURIER' | 'ON_THE_WAY' | 'DELIVERED' | 'CANCELLED';

export type HistoryOrder = {
    id: number;
    orderNumber: string;
    /** "VLO-007"; null for orders paid before daily numbers. */
    dailyNumber: string | null;
    /** The restaurant's own day, yyyy-MM-dd. */
    day: string | null;
    /** When it was paid, UTC. */
    placedAt: string | null;
    customerName: string | null;
    customerPhone: string | null;
    items: { name: string | null; quantity: number; extras?: string[] | null }[];
    /** What the customer paid for the food - all a restaurant ever gets. */
    foodTotal: number;
    /** Admins only (null for a restaurant): grand total, fees, credit, who delivered. */
    orderTotal: number | null;
    deliveryFee: number | null;
    serviceFee: number | null;
    creditRedeemed: number | null;
    paymentType: 'CASH' | 'ONLINE' | null;
    status: OrderStatus;
    outcome: OrderHistoryOutcome;
    /** Why the restaurant turned it down, as it chose it. */
    cancelReason: string | null;
    deliveredBy: 'WOLT' | 'VELLO_COURIER' | null;
    courierName: string | null;
    /** Admin only: absent for a restaurant login (Ariel, 25 Oct 2026). */
    src?: OrderSource | null;
    /** Earlier orders on Vello, any venue (0 = NC). Admin only: absent for a restaurant login. */
    prev?: number | null;
    restaurantId: number;
    restaurantName: string | null;
    currency: string;
    timeZone: string;
    /** DELIVERY, DINE_IN or PICKUP (absent from an older backend = delivery). */
    orderType?: OrderType | null;
    dineIn?: DineInInfo | null;
};

export type OrderHistory = {
    from: string;
    to: string;
    allVenues: boolean;
    restaurantId: number | null;
    restaurantName: string | null;
    timeZone: string;
    /** Null when the restaurants in view use different currencies. */
    currency: string | null;
    total: number;
    summary: {
        orders: number;
        /** The NC/RC and app/website counts: admin only, absent for a restaurant login. */
        newCustomers?: number;
        returningCustomers?: number;
        ios?: number;
        android?: number;
        web?: number;
        /** Dine-in orders in the range (absent from an older backend). */
        dineIn?: number;
        /** Pickup orders in the range (absent from an older backend). */
        pickup?: number;
        food: { currency: string; amount: number }[];
    };
    outcomes: { all: number; open: number; delivered: number; rejected: number; cancelled: number };
    orders: HistoryOrder[];
};

export type OrderHistoryQuery = {
    /** Admins only; null = every restaurant. Ignored by the server for a restaurant login. */
    locationId?: number | null;
    from: string;
    to: string;
    outcome?: OrderHistoryOutcome | null;
    customer?: 'new' | 'returning' | null;
    src?: 'app' | 'web' | null;
    type?: 'delivery' | 'dine_in' | 'pickup' | null;
};

export default class OrderHistoryService {
    async list(query: OrderHistoryQuery, start: number, end: number): Promise<OrderHistory> {
        try {
            const params: Record<string, string | number> = { from: query.from, to: query.to, _start: start, _end: end };
            if (query.locationId) params.locationId = query.locationId;
            if (query.outcome) params.outcome = query.outcome.toLowerCase();
            if (query.customer) params.customer = query.customer;
            if (query.src) params.src = query.src;
            if (query.type) params.type = query.type;
            const response = await axiosInstance.get('/vendor/orders/history', { params });
            return response.data;
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'order-history' } });
            throw e;
        }
    }
}
